import { useEffect } from 'react'
import { useRouterState } from '@tanstack/react-router'
import { isPrivatePath } from '../lib/i18n'
import { afterLoadAndIdle } from '../lib/runtime/after-load-idle'

/**
 * Single source of truth for the Foyer support chat widget. Swapping the bot or
 * the CDN later is a one-line edit here - nothing else references these.
 */
const FOYER_BOT_ID = 'bot_8c4cdf1b0d9244589c808312'
const FOYER_SRC = 'https://chatbot-webapp-widget.vercel.app/widget.js'
const SCRIPT_ID = 'foyer-widget'
const FOYER_MIN_DELAY_MS = 4000

/**
 * Loads the Foyer chat widget (bottom-right, self-rendered) on PUBLIC marketing
 * and content pages only.
 *
 * It is deliberately NOT loaded inside the private account / admin / auth area
 * (progress, saved, settings*, admin*, onboarding, login, register - in every
 * locale). That scope is decided by the shared {@link isPrivatePath} route
 * classifier, which is derived from the same `indexable` flags that already gate
 * sitemaps and hreflang, so the widget's reach tracks the route map with zero
 * duplication.
 *
 * The loader is injected client-side with a stable `id` so it is idempotent
 * across React StrictMode's double-invoked effects and any remount (we never
 * want two copies of the SDK), mirroring how {@link PortfolioAnalytics} loads
 * its tracker. The tag matches the embed snippet verbatim (`defer` + the two
 * `data-*` attributes). The widget is domain-locked to the production host, so
 * it intentionally does not render on localhost. Renders nothing.
 */
export function FoyerWidget() {
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  })
  const show = !isPrivatePath(pathname)

  useEffect(() => {
    if (typeof document === 'undefined') return

    const existing = document.getElementById(SCRIPT_ID)

    // On a private route never load the marketing widget. Remove our loader tag
    // if a prior public page injected it - defensive only, since the site
    // navigates via full document loads, so this is a no-op in practice.
    if (!show) {
      existing?.remove()
      return
    }

    if (existing) return

    // Third-party script + its config fetch: never on the critical path (M6).
    // Nobody needs the chat launcher in the first seconds, so it waits for
    // load + idle and at least FOYER_MIN_DELAY_MS after navigation start.
    return afterLoadAndIdle(
      () => {
        if (document.getElementById(SCRIPT_ID)) return
        const script = document.createElement('script')
        script.id = SCRIPT_ID
        script.src = FOYER_SRC
        script.defer = true
        script.setAttribute('data-bot-id', FOYER_BOT_ID)
        script.setAttribute('data-cfasync', 'false')
        document.body.appendChild(script)
      },
      { minDelayMs: FOYER_MIN_DELAY_MS },
    )
  }, [show])

  return null
}
